import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  CreateTemplateMessage,
  CreateTemplateResponse,
  CreateTemplateResponseData,
  DeleteTemplateResponse,
  DeleteTemplateResponseData,
  PublishTemplateResponse,
  PublishTemplateResponseData,
  Template,
  TemplateLookupMessage,
  TemplateLookupResponse,
  TemplateLookupResponseData,
  TemplateMessage,
} from './template';
import { Injectable } from '@angular/core';

@Injectable()
export class TemplateApiService {
  constructor(private http: HttpClient) {}

  lookup(lookupMessage: TemplateLookupMessage): Observable<TemplateLookupResponse> {
    const url = '/api/v2/template/lookup/';
    const uniquePartnerIds = lookupMessage.partnerIds.filter((v, i) => lookupMessage.partnerIds.indexOf(v) === i);
    const params = {
      params: new HttpParams({
        fromObject: {
          partner_ids: uniquePartnerIds,
          include_unpublished: JSON.stringify(lookupMessage.includeUnpublished),
          cursor: lookupMessage.cursor,
          creating: JSON.stringify(lookupMessage.creating),
        },
      }),
    };
    return this.http.get(url, params).pipe(
      map((resp: TemplateLookupResponseData) => {
        const templates = resp.data.templates.map((t) => TemplateMessage.fromApi(t));
        const sortedTemplates = templates.sort((a: Template, b: Template) => {
          return a.created.getTime() - b.created.getTime();
        });
        return {
          templates: sortedTemplates,
          cursor: resp.data.cursor_string,
        } as TemplateLookupResponse;
      }),
    );
  }

  publishTemplate(siteId: string): Observable<PublishTemplateResponse> {
    const url = `/api/v2/template/publish/${siteId}/`;
    return this.http.post(url, {}).pipe(
      map(
        (resp: PublishTemplateResponseData) =>
          ({
            jobId: resp.data.job_id,
            siteId: resp.data.site_id,
          } as PublishTemplateResponse),
      ),
    );
  }

  createTemplate(createMessage: CreateTemplateMessage): Observable<CreateTemplateResponse> {
    const url = '/api/v2/template/create/';
    return this.http.post(url, createMessage.toApiMessage()).pipe(
      map(
        (resp: CreateTemplateResponseData) =>
          ({
            templateId: resp.data.template_id,
            jobId: resp.data.job_id,
          } as CreateTemplateResponse),
      ),
    );
  }

  deleteTemplate(siteId: string): Observable<DeleteTemplateResponse> {
    const url = `api/v2/template/delete/${siteId}/`;
    return this.http.post(url, {}).pipe(
      map(
        (resp: DeleteTemplateResponseData) =>
          ({
            siteId: resp.data.site_id,
            jobId: resp.data.job_id,
          } as DeleteTemplateResponse),
      ),
    );
  }

  getIsTemplateDistributed(siteId: string): Observable<boolean> {
    const url = `api/v2/site/${siteId}/template-distributed/`;
    return this.http.get(url, {}).pipe(
      map((resp: any) => resp.data?.is_template_distributed ?? false),
      catchError((err) => throwError(() => err)),
    );
  }

  setTemplateAsDistributed(siteId: string, isDistributed: boolean): Observable<boolean> {
    const url = `api/v2/site/${siteId}/template-distributed/`;
    const body = { is_template_distributed: isDistributed };
    return this.http.post(url, body).pipe(
      map((resp: any) => resp.data?.success ?? true),
    );
  }
}
